import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import _ from "lodash";
import "./booking-summary.css";
import ExtraRequestTooltip from "../reservation-details/Extra-request-tooltip";
import EditSpecialRequest from "../reservation-details/edit-special-request";
import OverallPriceInfoToolTip from "../reservation-details/overall-price-tooltip";
import CurrencyConvertor from "../../global/currency-convertor";
import UserInputViewer from "../../global/UserInputViewer";
import { toSentenceCase } from "../../../utils/formatting";

function Skeleton({ height = 19.5, width = "100%", marginBottom = 0 }) {
  return (
    <div
      className="skeleton-loading"
      style={{
        height: `${height}px`,
        width,
        borderRadius: "var(--border-radius-sm)",
        marginBottom: `${marginBottom}px`,
      }}
    ></div>
  );
}

const BookingSummary = ({ singleReservation = {}, loading = true }) => {
  const { currency, exchange_rate } = useSelector(
    (state) => state.user_preference
  );

  const [specialRequest, setSpecialRequest] = React.useState(
    singleReservation?.extraDetails
  );

  const formattedBookingDateTime = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZone: "UTC",
    timeZoneName: "short",
  }).format(new Date(singleReservation?.bookingDate || Date.now()));

  const totalAdults = singleReservation?.totalAdults;
  const totalChilds = singleReservation?.totalChilds;

  const reservationType = singleReservation?.type;

  const paymentType = singleReservation?.paymentType;

  const status = singleReservation?.status;

  const adults = `${totalAdults} ${totalAdults > 1 ? "adults" : "adult"}`;
  const childs = ` , ${totalChilds} ${totalChilds > 1 ? "children" : "child"}`;

  // duplicating by quantity
  const duplicatedCotsRequested = singleReservation?.totalCribs?.flatMap(
    (item) => Array(item.quantity).fill(item)
  );
  const cotsRequestedCount = duplicatedCotsRequested?.length;

  // duplicating by quantity
  const duplicatedExtraBedsRequested =
    singleReservation?.totalExtraBeds?.flatMap((item) =>
      Array(item.quantity).fill(item)
    );
  const extrabedsRequestedCount = duplicatedExtraBedsRequested?.length;

  const hippoOfferAmount = singleReservation?.hippoOffer?.discountedAmount ?? 0;

  const overallPriceInfo = {
    tax: (singleReservation?.totalTax * exchange_rate).toFixed(2) || 0,
    taxRates: singleReservation?.taxRates?.map((tax) => ({
      ...tax,
      amount: (tax.amount * exchange_rate).toFixed(2) || 0,
    })),
    totalPrice: (singleReservation?.totalPrice * exchange_rate).toFixed(2) || 0,
    discount:
      (
        (singleReservation?.discount - hippoOfferAmount) *
        exchange_rate
      ).toFixed(2) || 0,

    hippoOfferAmount: (hippoOfferAmount * exchange_rate).toFixed(2) || 0,
    grandTotal: (singleReservation?.grandTotal * exchange_rate).toFixed(2) || 0,
    currency,
  };

  useEffect(() => {
    setSpecialRequest(singleReservation?.extraDetails);
  }, [singleReservation]);

  let discountType = "";

  if (singleReservation?.isHippoOfferApplied) {
    discountType = "hippoOffer";
  } else if (singleReservation?.isCustomPromotionApplied) {
    discountType = "customPromotion";
  }

  const EditSpecialRequestShow = singleReservation.reservationPhase !== "PAST";

  return (
    <div className="user-details-wrapper">
      <div className="row">
        <div className="col-12 ">
          <div className="rd-label">Guest name</div>
          <UserInputViewer
            text={!loading ? singleReservation?.hostName : ""}
            tag="div"
            className="rd-value rd-value-fs-15"
            style={{
              textTransform: "capitalize",
            }}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-6 ">
          <div className="rd-label">Check in</div>
          <div className="rd-value rd-value-fs-15">
            {loading && <Skeleton height={19.5} />}
            {!loading && singleReservation?.checkinDate}
          </div>
        </div>
        <div className="col-6 ">
          <div className="rd-label">Check out</div>
          <div className="rd-value rd-value-fs-15">
            {loading && <Skeleton height={19.5} />}
            {!loading && singleReservation?.checkoutDate}
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-6 ">
          <div className="rd-label">Check in From</div>
          <div className="rd-value">
            {loading && <Skeleton height={19.5} />}
            {!loading && getTime(singleReservation?.checkinTime?.from)}
          </div>
        </div>
        <div className="col-6 ">
          <div className="rd-label">Check in To</div>
          <div className="rd-value">
            {loading && <Skeleton height={19.5} />}
            {!loading && getTime(singleReservation?.checkinTime?.to)}
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-6 ">
          <div className="rd-label">Stays</div>
          <div className="rd-value">
            {loading && <Skeleton height={19.5} />}
            {!loading && singleReservation?.totalNights}
          </div>
        </div>
        <div className="col-6 ">
          <div className="rd-label">Total rooms</div>
          <div className="rd-value">
            {loading && <Skeleton height={19.5} />}
            {!loading && singleReservation?.totalRooms}
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-6 ">
          <div className="rd-label">Reservation ID</div>
          <div className="rd-value">
            {loading && <Skeleton height={19.5} />}
            {!loading && `#${singleReservation?.id}`}
          </div>
        </div>
        <div className="col-6">
          <div className="rd-label">Total guests</div>
          <div className="rd-value">
            {loading && <Skeleton height={19.5} />}

            {!loading && adults}
            {!loading && childs}
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-6">
          <div className="rd-label">Reservations type</div>
          <div className="rd-value">
            {loading && <Skeleton height={19.5} />}
            {!loading && toSentenceCase(reservationType)}
          </div>
        </div>
        <div className="col-6 ">
          <div className="rd-label">Payment type</div>
          <div className="rd-value">
            {loading && <Skeleton height={19.5} />}
            {!loading && toSentenceCase(paymentType)}
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="rd-label">Booked on</div>
          <div className="rd-value">
            {loading && <Skeleton height={19.5} />}
            {!loading && formattedBookingDateTime}
          </div>
        </div>
      </div>

      {loading && (
        <div className="row">
          <div className="col-6 ">
            <RequestedSkeleton type={"cot"} />
          </div>
          <div className="col-6 ">
            <RequestedSkeleton type={"bed"} />
          </div>
        </div>
      )}
      {!loading && (cotsRequestedCount > 0 || extrabedsRequestedCount > 0) && (
        <div className="row">
          {cotsRequestedCount > 0 && (
            <div className="col-6">
              <div className="rd-label">Cots requested</div>
              <div className="rd-value rd-value-with-tooltip">
                {cotsRequestedCount}{" "}
                <ExtraRequestTooltip
                  request_info={duplicatedCotsRequested}
                  request_type="Extra Cot"
                  currency={currency}
                  exchange_rate={exchange_rate}
                />
              </div>
            </div>
          )}
          {extrabedsRequestedCount > 0 && (
            <div className="col-6">
              <div className="rd-label">Extra beds requested</div>
              <div className="rd-value rd-value-with-tooltip">
                {extrabedsRequestedCount}{" "}
                <ExtraRequestTooltip
                  request_info={duplicatedExtraBedsRequested}
                  request_type="Extra Bed"
                  currency={currency}
                  exchange_rate={exchange_rate}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {loading && <ExtraRequestSkeleton />}

      {singleReservation?.extraDetails && (
        <div className="row">
          <div className="col-12 ">
            <div className="rd-label">Special request</div>
            <UserInputViewer
              className="rd-value"
              style={{ textAlign: "justify" }}
              tag="div"
              text={specialRequest}
            />

            {EditSpecialRequestShow && (
              <EditSpecialRequest
                specialRequest={specialRequest}
                setSpecialRequest={setSpecialRequest}
                bookingId={singleReservation.id}
              />
            )}
          </div>
        </div>
      )}

      <div className="row">
        <div className="col-6 ">
          <div className="rd-label">Total</div>
          <div
            className="rd-value"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "3px",
            }}
          >
            {loading && <Skeleton height={19.5} width={100} />}
            {!loading && (
              <>
                <CurrencyConvertor
                  price={singleReservation?.grandTotal}
                  primaryDiscountType={discountType}
                />
                <OverallPriceInfoToolTip overallPriceInfo={overallPriceInfo} />
              </>
            )}
          </div>
        </div>
        <div className="col-6 ">
          <div className="rd-label">Status</div>
          <div
            className="rd-value"
            style={{
              color: `${
                (status === "ACTIVE" && "green") ||
                (status === "CANCELLED" && "red") ||
                (status === "NO_SHOW" && "brown")
              }`,
            }}
          >
            {loading && <Skeleton height={19.5} />}
            {!loading && toSentenceCase(status)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingSummary;

function ExtraRequestSkeleton() {
  return (
    <div className="col-12 ">
      <div className="rd-label">Special request</div>
      <div className="rd-value" style={{ textAlign: "justify" }}>
        <>
          <Skeleton height={19.5} marginBottom={5} />
          <Skeleton height={19.5} marginBottom={5} />
          <Skeleton height={19.5} width={"80%"} />
        </>
      </div>
    </div>
  );
}

function RequestedSkeleton({ type }) {
  const text = type === "cot" ? "Cots" : "Extra beds";
  return (
    <div className="col-12">
      <div className="rd-label">{text} requested</div>
      <div className="rd-value">
        <Skeleton height={19.5} width={"100%"} />
      </div>
    </div>
  );
}

function getTime(dateTimeString) {
  if (!dateTimeString) {
    return "N/A";
  }

  const date = new Date(dateTimeString);
  const hours = String(date.getUTCHours()).padStart(2, "0");
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}
